import { action, computed, observable, makeObservable } from 'mobx';
import { ApiRoutes } from '../routes/ApiRoutes';
import { TemplatesStore } from './TemplatesStore';
import { RoutingStore } from './RoutingStore';
import { CategoriesStore } from './CategoriesStore';

export type Screen = 'templates' | 'navigation' | 'details' | 'preview';

const isLandscapeQuery = `only screen and (orientation: landscape)`;

export interface ScreenStoreInitialState {
  screen: Screen;
}

export class ScreenStore {
  public explicitScreen: Screen = 'templates';

  public isLandscape: boolean = false;

  public isIframeLoaded: boolean = false;

  public get isSinglePage(): boolean {
    return !!(['preview', 'details'].includes(this.screen) && this.templatesStore.expandedTemplate);
  }

  public get screen(): Screen {
    if (this.explicitScreen === 'preview') {
      return typeof this.templatesStore.expandedTemplate !== 'undefined' ? 'preview' : 'templates';
    }

    return this.explicitScreen;
  }

  private orientationMedia: MediaQueryList;

  constructor(
    private routingStore: RoutingStore,
    private templatesStore: TemplatesStore,
    private categoriesStore: CategoriesStore,
    private apiRoutes: ApiRoutes,
    initialState: ScreenStoreInitialState,
  ) {
    makeObservable<ScreenStore, 'handleOrientationChange'>(this, {
      explicitScreen: observable,
      isLandscape: observable,
      isIframeLoaded: observable,
      isSinglePage: computed,
      screen: computed,
      previewIframeUrl: computed,
      handleOrientationChange: action,
      setIframeLoaded: action,
      goToScreen: action,
      handlePopState: action,
    });

    if (initialState) {
      this.explicitScreen = initialState.screen;
    }
  }

  get previewIframeUrl() {
    return this.isSinglePage ? this.apiRoutes.preview(this.templatesStore.expandedTemplate.metaSiteId) : null;
  }

  public initOrientationHandler() {
    if (this.orientationMedia) {
      return;
    }
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.orientationMedia = window.matchMedia(isLandscapeQuery);
      this.orientationMedia.addEventListener('change', this.handleOrientationChange);
      this.handleOrientationChange(this.orientationMedia);
    }
  }

  public openSidebar() {
    this.goToScreen('navigation', { pushState: false });
  }

  public closeSidebar() {
    this.goToScreen('templates', { pushState: false });
  }

  private handleOrientationChange = (mq: MediaQueryList | MediaQueryListEvent) => {
    this.isLandscape = mq.matches;
  };

  setIframeLoaded(isLoaded: boolean) {
    this.isIframeLoaded = isLoaded;
  }

  goToScreen(
    screen: Screen,
    {
      expandedTemplateId,
      isSwitchedByVO = false,
      pushState = true,
    }: { expandedTemplateId?: string; isSwitchedByVO?: boolean; pushState?: boolean } = {},
  ) {
    this.explicitScreen = screen;

    if (screen === 'templates') {
      this.templatesStore.expandedTemplate?.setInfoShown(false);
    } else if (expandedTemplateId) {
      this.templatesStore.templates.find(({ id }) => id === expandedTemplateId)?.setInfoShown(true, isSwitchedByVO);
    }

    if (pushState) {
      this.pushScreenStateToHistory({ screen, expandedTemplateId });
    }
  }

  pushScreenStateToHistory({ screen, expandedTemplateId }: { screen: Screen; expandedTemplateId?: string }) {
    const { state: currentState } = history;
    const isStateChanged =
      !currentState || screen !== currentState.screen || expandedTemplateId !== currentState.expandedTemplateId;
    if (isStateChanged) {
      history.pushState({ expandedTemplateId, screen }, '');
    }
  }

  handlePopState = (event: PopStateEvent) => {
    const screen: Screen | undefined = event.state?.screen;
    const expandedTemplateId: string | undefined = event.state?.expandedTemplateId;

    const screensAvailableFromHistory: Screen[] = ['details', 'preview'];
    if (screen) {
      this.goToScreen(screen, { pushState: false, expandedTemplateId });
    } else if (screensAvailableFromHistory.includes(this.explicitScreen)) {
      // state record written by route change (via react-router)
      this.goToScreen('templates', { pushState: false });
    }
  };

  public serialize(): ScreenStoreInitialState {
    return {
      screen: this.screen,
    };
  }
}
